
import { Document } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

// PERFORMANCE: Removed unnecessary imports and local storage fallbacks

// Add a document to a lead
export const addDocument = async (
  leadId: string, 
  name: string, 
  url: string, 
  type: string,
  description?: string
): Promise<Document> => {
  try {
    const userData = await supabase.auth.getUser();
    const userId = userData.data.user?.id;
    
    const newDocument = {
      name,
      url,
      type,
      description,
      lead_id: leadId,
      uploaded_by: userId,
    };
    
    // Insert into Supabase
    const { data, error } = await supabase
      .from('documents')
      .insert(newDocument as any)
      .select()
      .single();

    if (error) throw error;
    
    // Return the formatted document
    return {
      id: data?.id || '',
      name: data?.name || '',
      url: data?.url || '',
      type: data?.type || 'unknown',
      description: data?.description || description || '', // Use the input description or the DB value
      leadId: leadId,
      createdAt: data?.created_at || '',
      uploadedBy: data?.uploaded_by || userId || undefined, // Include the uploadedBy field
    };
  } catch (error) {
    console.error('Error adding document to Supabase:', error);
    throw error;
  }
};

// Delete a document
export const deleteDocument = async (leadId: string, documentId: string): Promise<void> => {
  try {
    // Get document details to check if it's stored in Supabase Storage
    const { data: documentData } = await supabase
      .from('documents')
      .select('url')
      .eq('id', documentId)
      .single();

    // If document is stored in Supabase Storage, delete it from there first
    if (documentData?.url && documentData.url.includes('storage/v1/object/public/documents/')) {
      const filePath = documentData.url.split('/documents/')[1];
      if (filePath) {
        const { error: storageError } = await supabase
          .storage
          .from('documents')
          .remove([filePath]);
        
        if (storageError) {
          console.error('Error deleting file from storage:', storageError);
        }
      }
    }

    // Delete from Supabase documents table
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting document from Supabase:', error);
    throw error;
  }
};

// Upload a file to Supabase Storage and create a document record
export const uploadDocumentFile = async (
  leadId: string,
  file: File,
  description?: string
): Promise<Document> => {
  try {
    // Create a unique file path to prevent collisions
    const timestamp = new Date().getTime();
    const filePath = `${leadId}/${timestamp}_${file.name}`;
    
    // Verify the user is authenticated before upload
    const { data: userData, error: authError } = await supabase.auth.getUser();
    if (authError || !userData.user) {
      throw new Error('Authentication required to upload files');
    }
    
    // Upload file to Supabase Storage
    const { data: storageData, error: storageError } = await supabase
      .storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (storageError) {
      console.error('Storage error:', storageError);
      throw new Error(`Upload failed: ${storageError.message}`);
    }
    
    // Get the public URL for the uploaded file
    const { data: publicUrlData } = supabase
      .storage
      .from('documents')
      .getPublicUrl(filePath);
    
    if (!publicUrlData.publicUrl) {
      throw new Error('Failed to get public URL for uploaded file');
    }
    
    // Determine file type based on extension
    const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
    let fileType = 'document';
    
    if (['pdf'].includes(fileExt)) {
      fileType = 'pdf';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt)) {
      fileType = 'image';
    } else if (['zip', 'rar', '7z'].includes(fileExt)) {
      fileType = 'archive';
    }
    
    // Create document record in the database
    const newDocument = await addDocument(
      leadId,
      file.name,
      publicUrlData.publicUrl,
      fileType,
      description
    );
    
    return newDocument;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};
